import { useEffect, useState, useRef } from "react";
import { Headline1, Subtitle2 } from "components/shareable/Typography";
import UpgradeToProLabel from "components/shareable/UpgradeToProLabel";
import BillingPlanOptions from "components/shareable/UpgradeToProModal/Components/BillingPlanOptions";
import { BillingCycle, format, labels } from "components/shareable/UpgradeToProModal/utils";
import Sty, { Container, StyledLabelP, SymphonyTooltip } from "modules/Sty";
import { PlaylistingCampaignInput, YouTubeCampaignData } from "modules/Types";
import getSymbolFromCurrency from "currency-symbol-map";
import {
    CheckoutType,
    PRO_MONTHLY_PLAN_PRICE,
    TEAM_MONTHLY_PLAN_PRICE,
    TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE,
    PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
    TEAM_ANNUAL_BY_MONTH_PLAN_PRICE,
    ONE_OFF_CAMPAIGN_PRICE,
    DEFAULT_CURRENCY,
    AdPlatform
} from "modules/Const";
import { ReactComponent as QuestionMarkIcon } from "assets/images/question-mark.svg";
import Intercom from "helpers/Intercom";
import { getNumberWithDecimals } from "helpers/General";
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import useOrganization from 'Hooks/useOrganization';
import { clsx, styx } from 'modules/Utils';
import SymphonyTabs from "elements/SymphonyTabs";
import Colors from "modules/Colors";
import useCurrentUser from "Hooks/useCurrentUser";
import ButtonSymphony from "elements/ButtonSymphony";
import { getConvertedCurrency_api } from "api/campaignApi";
import PayPalButtonGroup from "pages/MarketingPage/Components/GoLiveCard/PaypalCheckoutOption";
import useCampaign from "Hooks/useCampaign";
import { toastError } from "modules/Toast";
import { useHistory } from "react-router-dom";
import { CSSProperties } from "react";
import TagSymphony, { TagColors } from "elements/TagSymphony";
import { track } from "analytics";
import useFacebookConnections from "Hooks/useFacebookConnections";

type CurrencyPrices = {
    annualConverted: number,
    monthlyConverted: number,
    oneOffConverted: number
}

const SubmitOrCheckoutButtonsView = ({
    overrideCampaignInput,
    style
}: {
    /** Used for old campaigns not supported in new Campaign store */
    overrideCampaignInput?:
    YouTubeCampaignData | PlaylistingCampaignInput,
    style?: React.CSSProperties
}) => {
    const history = useHistory();
    const {
        organization: primaryOrganization,
        brandIdIsInOrganization,
    } = useOrganization()
    const { currentBrand, isProBrand, getAffiliatePartnerDetails } = useCurrentBrand() as BrandExistsState
    const { proFreeTrialRedeemed } = useCurrentUser()
    const {
        submitCampaign,
        campaignAllowsDirectPayment,
        campaignDoesntRequireUpgrade,
        campaignDisclaimer,
        isSubmittingCampaign,
        adPlatform,
        playbookType,
        isGeographyBucketsObjectValid,
    } = useCampaign()

    let {
        canRunAds
    } = useFacebookConnections()

    if (adPlatform !== AdPlatform.META) {
        canRunAds = true
    }

    const allowsDirectPayment = campaignAllowsDirectPayment()
    const doesntRequireUpgrade = campaignDoesntRequireUpgrade()

    const [currencyPrices, setCurrencyPrices] = useState<CurrencyPrices>({
        annualConverted: PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
        monthlyConverted: PRO_MONTHLY_PLAN_PRICE,
        oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
    })

    const [billingCycle, setBillingCycle] = useState<BillingCycle>(BillingCycle.MONTHLY)

    const currencyCode = currentBrand.currency?.code ?? DEFAULT_CURRENCY

    const freeTrialOver = proFreeTrialRedeemed()
    // get the currency codes
    const currencySymbol = getSymbolFromCurrency(currencyCode)

    const [monthlyAdditionalProPlanPrice, setMonthlyAdditionalProPlanPrice] = useState<number>(TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE)

    // for non-Pro users, we show the option to run a one-off campaign or upgrade to Pro
    const [selectedTab, setSelectedTab] = useState<CheckoutType>(CheckoutType.ONE_OFF_CAMPAIGN)

    const paypalOrderDetails = useRef<any>(); // Reference to store PayPal order details

    // Styles for the component
    const sty: Record<string, CSSProperties> = {
        billingOptionsContainer: {
            marginBottom: 16
        },
        disclaimer: {
            marginBottom: 16,
            fontSize: 16,
            color: '#707070',
            fontWeight: 400
        },
        buttonContainer: {
            marginTop: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: '100%'
        },
        upgradeView: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
        },
        freeTrialPrice: {
            color: '#16a34a',
            fontWeight: 600,
        },
        regularPrice: {
            color: '#000',
            fontWeight: 500,
            marginLeft: 2,
            transform: 'translateY(2px)'
        },
        pricingContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            backgroundColor: '#f5f8ff',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #e5e9f2'
        },
        pricingRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 8
        },
        tagsRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 4
        }
    }

    // this is used ONLY for international currencies - specifically when a user selects an ad account with an international currency, and
    // we need to convert the "Upgrade to Pro" price to the international currency
    const getPricesValues = async () => {
        let baseMonthly;
        let baseAnnual;
        let oneOffCampaign;

        if (primaryOrganization && primaryOrganization.status === 'ready') {
            baseMonthly = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: TEAM_MONTHLY_PLAN_PRICE })
            baseAnnual = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: TEAM_ANNUAL_BY_MONTH_PLAN_PRICE })
        } else {
            baseMonthly = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: PRO_MONTHLY_PLAN_PRICE })
            baseAnnual = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: PRO_ANNUAL_BY_MONTH_PLAN_PRICE })
        }

        oneOffCampaign = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: ONE_OFF_CAMPAIGN_PRICE })

        setCurrencyPrices({
            annualConverted: Number(getNumberWithDecimals(baseAnnual, 2)),
            monthlyConverted: Number(getNumberWithDecimals(baseMonthly, 2)),
            oneOffConverted: Number(getNumberWithDecimals(oneOffCampaign, 2))
        })
    }

    useEffect(() => {
        if (currencyCode !== DEFAULT_CURRENCY) {
            if (primaryOrganization && primaryOrganization.status === 'ready') {
                getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE })
                    .then((res) => setMonthlyAdditionalProPlanPrice(Number(getNumberWithDecimals(res, 2))))
            }
            getPricesValues()
        } else {
            // TODO: remove this else statement when we fix the way we switch from brand to brand, right now it keeps some values unchanged
            if (currencyPrices.monthlyConverted !== PRO_MONTHLY_PLAN_PRICE) {
                setCurrencyPrices({
                    annualConverted: PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
                    monthlyConverted: PRO_MONTHLY_PLAN_PRICE,
                    oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
                })
            }
            if (primaryOrganization && primaryOrganization.status === 'ready') {
                setCurrencyPrices({
                    annualConverted: TEAM_ANNUAL_BY_MONTH_PLAN_PRICE,
                    monthlyConverted: TEAM_MONTHLY_PLAN_PRICE,
                    oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
                })
            }
        }
    }, [currencyCode])

    /** 
     * Creates a PayPal order through the campaign checkout API
     * This is used for both Pro users and one-off campaigns
     * @returns Promise resolving to the PayPal order ID
     */
    const createPaypalOrder = async () => {
        try {
            if (isSubmittingCampaign) return '';

            // Set loading state
            // isSubmittingCampaign is already managed by the useCampaign hook

            // Determine which checkout type to use based on the selected tab
            const checkoutType = selectedTab === CheckoutType.ONE_OFF_CAMPAIGN ?
                'oneOffCheckout' : 'upgradePlanCheckout';

            // Make API call to create checkout session
            // We use the same submitCampaign function for both card and PayPal
            const response = await submitCampaign({
                submitType: checkoutType,
                recurring_interval: billingCycle,
                overrideCampaignInput
            });

            if (!response) {
                throw new Error('Failed to create PayPal order');
            }

            // Store order details for use in onApprove callback
            paypalOrderDetails.current = {
                orderId: response?.paypalOrderId || 'mock-order-id', // PayPal order ID from API
                campaignId: response?.campaign?.id || response?.id   // Campaign ID to redirect to later
            };

            return paypalOrderDetails.current.orderId;
        } catch (error) {
            console.error('Error creating PayPal order:', error);
            toastError('There was an error creating your PayPal order. Please try again.');
            return '';
        }
    }

    /**
     * Handles cancellation of PayPal checkout flow
     */
    const onCancelPayPalOrder = () => {
        // Reset loading state if needed
        // Track cancellation for analytics if necessary
        console.log('PayPal checkout cancelled');
        return Promise.resolve();
    }

    /**
     * Handles errors in the PayPal checkout flow
     * @param error The error object from PayPal
     */
    const onErrorPayPalOrder = () => {
        console.error('PayPal error occurred');
        toastError('There was an error processing your PayPal payment. Please try again.');
        return Promise.resolve();
    }

    /**
     * Handles successful PayPal order approval
     * Redirects to the campaign submission success page
     */
    const onPayPalOrderApprove = async () => {
        try {
            if (!paypalOrderDetails.current) {
                throw new Error('PayPal order details not found');
            }

            const { campaignId } = paypalOrderDetails.current;

            if (campaignId) {
                // Navigate to the campaign submission success page
                history.push(`/marketing2/submission/${campaignId}`);
            } else {
                throw new Error('Campaign ID not found in PayPal order details');
            }

            return Promise.resolve();
        } catch (error) {
            console.error('Error processing PayPal approval:', error);
            toastError('There was an error processing your payment. Please contact support.');
            return Promise.resolve();
        }
    }

    /**
     * Renders the PayPal button component
     * @param allowNonPro Whether to allow non-Pro users to use PayPal (for one-off campaigns)
     */
    const renderPaypalButton = (allowNonPro: boolean = false) => {
        return null
        return (
            <PayPalButtonGroup
                createOrder={createPaypalOrder}
                onCancel={onCancelPayPalOrder}
                onError={onErrorPayPalOrder}
                onApprove={onPayPalOrderApprove}
                className="w-full"
                currencyCode={currencyCode}
                style={{ marginTop: 8 }}
                allowWithoutPro={allowNonPro}
            />
        )
    }

    const handleClickSubmitButton = async (submitType: 'directCreate' | 'upgradePlanCheckout' | 'oneOffCheckout') => {
        try {
            let res = await submitCampaign({ submitType, recurring_interval: billingCycle, overrideCampaignInput });

            // If we've redirected to Stripe already (happens in upgradePlanCheckout case), don't do anything else
            if (res?.redirected) {
                // Already redirected to Stripe, nothing more to do
                return;
            }

            track("Submit Campaign", {
                adPlatform,
                playbookType,
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name,
            })

            // For direct creation or when the user is already on a paid plan (no URL redirect needed)
            if (res?.campaign?.id) {
                // Navigate to the campaign submission success page
                history.push(`/marketing2/submission/${res.campaign.id}`);
            } else if (submitType === 'directCreate' && res?.id) {
                // Handle case where response directly contains the campaign ID
                history.push(`/marketing2/submission/${res.id}`);
            }
        } catch (error) {
            console.error('error: ', error);
            toastError('There was an error submitting your campaign. Please try again.');
        }
    }

    /**
     * Renders the view for Pro users, showing submission UI
     */
    const renderProView = () => {

        let mainComponent;
        if (allowsDirectPayment) {
            mainComponent = (
                <>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        marginTop: 16
                    }}>
                        <ButtonSymphony
                            className="submitCampaignOrUpgradeButton"
                            width="100%"
                            onClick={() => handleClickSubmitButton('oneOffCheckout')}
                            disabled={isSubmittingCampaign || !isGeographyBucketsObjectValid || !canRunAds}
                            isLoading={isSubmittingCampaign}
                        >
                            Checkout with Card
                        </ButtonSymphony>
                        {/* TODO MEGH: Re-add paypal button */}
                        {/* Pro users can always use PayPal if they have USD currency */}
                        {renderPaypalButton(false)}
                    </div>
                </>
            )
        } else {
            mainComponent = (
                <ButtonSymphony
                    className="submitCampaignButton1"
                    onClick={() => handleClickSubmitButton('directCreate')}
                    width="100%"
                    disabled={isSubmittingCampaign || !isGeographyBucketsObjectValid || !canRunAds}
                    isLoading={isSubmittingCampaign}
                >
                    Submit Campaign
                </ButtonSymphony>

            )
        }
        return (
            <div className={clsx(`submitCampaignOrUpgradeComponent`)} style={style}>
                <div className="campaignDisclaimer" style={styx(Sty.text16grey, {textAlign: 'center', marginBottom: 16})} >
                    {campaignDisclaimer}
                </div>
                {mainComponent}
            </div>
        );
    }

    const renderOneOffCampaignView = () => {
        let title = `Run a one-off campaign for ${currencySymbol}${currencyPrices.oneOffConverted}`
        let subtitle = 'You can run a campaign without subscribing. To unlock advanced ad analytics, we recommend upgrading to Pro.'

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    ...style
                }}
            >
                <Headline1 color={Colors.textColor}>
                    {title}
                </Headline1>
                <Subtitle2 color={Colors.textColor}>
                    {subtitle}
                </Subtitle2>
                <StyledLabelP color="#707070" fontSize={16} fontWeight={400}>
                    {campaignDisclaimer}
                </StyledLabelP>
                <ButtonSymphony
                    className="checkoutWithCardButton"
                    width="100%"
                    isLoading={isSubmittingCampaign}
                    disabled={isSubmittingCampaign || !isGeographyBucketsObjectValid || !canRunAds}
                    onClick={() => handleClickSubmitButton('oneOffCheckout')}
                >
                    Checkout with Card
                </ButtonSymphony>
                {/* Allow PayPal for one-off campaigns even for non-Pro users */}
                {currencyCode === 'USD' && renderPaypalButton(true)}
                <ButtonSymphony
                    className="unlockUnlimitedButton"
                    width="100%"
                    variant="outlined"
                    disabled={isSubmittingCampaign}
                    onClick={() => setSelectedTab(CheckoutType.UPGRADE_PLAN)}
                >
                    Unlock Unlimited Campaigns
                </ButtonSymphony>

            </div>

        )
    }

    const renderUpgradePlanView = () => {
        // Used to determine how to calculate price
        const fromPartner = getAffiliatePartnerDetails();

        // Either discountAmount, or 1x (no discount)
        const partnerDiscount: number = fromPartner && fromPartner.discountAmount ? fromPartner.discountAmount : 1;
        const { id: priceId, billedTrialText } = labels[billingCycle ?? 'monthly'];

        // The price, formatted properly for the international currency
        const formattedAmount = format(priceId === 'monthly'
            ? currencyPrices.monthlyConverted * partnerDiscount
            : currencyPrices.annualConverted * 12);

        // Original price (non-discounted)
        const originalFormattedAmount = format(priceId === 'monthly'
            ? currencyPrices.monthlyConverted
            : currencyPrices.annualConverted * 12);

        const formattedCurrency = currencySymbol;
        // The time period (monthly or annual)
        const formattedTimePeriod = billedTrialText;

        // Determine appropriate title based on trial status
        const title = freeTrialOver
            ? 'Upgrade to Pro to run AI-powered marketing campaigns.'
            : 'Unlock unlimited campaigns and advanced ad analytics with Pro.';

        const openIntercom = () => Intercom.openSymphonyProArticle();

        // Determine subtitle text based on partner status and trial status
        let subtitleText = '';
        if (fromPartner) {
            const showPartnerText = fromPartner.partnerName ? `${fromPartner.partnerName} artists ` : 'You\'ll';
            const partnerDiscountPercentage = 100 - (partnerDiscount * 100);

            if (freeTrialOver) {
                subtitleText = `${showPartnerText} get a ${partnerDiscountPercentage}% discount on monthly plans for their first 12 months!`;
            } else {
                subtitleText = `${showPartnerText} get a 7-day free trial + ${partnerDiscountPercentage}% discount on monthly plans for their first 12 months!`;
            }
        } else {
            if (freeTrialOver) {
                subtitleText = 'Unlock the full power of Symphony. Choose a plan below:';
            } else {
                subtitleText = 'Start your 7-day free trial and unlock the full power of Symphony.';
            }
        }

        const buttonText = !freeTrialOver ? 'Start Free Trial & Submit Campaign' : 'Upgrade & Submit Campaign';
        const trialText = !freeTrialOver
            ? `Free trial is for 7 days, then ${formattedCurrency}${formattedAmount}${currencyCode !== 'USD' ? ` ${currencyCode}` : ''} / ${formattedTimePeriod} unless you cancel. You'll get an email reminder 3 days before your trial ends.`
            : '';

        // Calculate the original price (without discount)
        const originalPrice = priceId === 'monthly'
            ? currencyPrices.monthlyConverted
            : currencyPrices.annualConverted * 12;

        // Calculate the discounted price (with partner discount if applicable)
        const discountedPrice = originalPrice * partnerDiscount;

        // Format both prices for display
        const discountedFormattedAmount = format(discountedPrice);

        return (
            <div
                className="upgradeProView"
                style={{
                    ...sty.upgradeView,
                    ...style
                }}
            >
                <Container display="flex" flexDirection="row" gap="8px" >
                    <UpgradeToProLabel
                        className="submitOrCheckoutButtonsViewUpgradeToProLabel"
                        label="Symphony Pro"
                        style={{
                            maxWidth: 160,
                        }}
                    />
                    <button onClick={openIntercom}>
                        <SymphonyTooltip
                            className="w-6 h-6"
                            title="Click here to learn more."
                            arrow
                        >
                            <QuestionMarkIcon height={24} width={24} />
                        </SymphonyTooltip>
                    </button>
                </Container>
                <Headline1 color={Colors.textColor}>
                    {title}
                </Headline1>
                <Subtitle2 color={Colors.textColor}>
                    {subtitleText}
                </Subtitle2>

                {/* Billing Plan Options */}
                <div className="billingPlanOptionsContainer" style={sty.billingOptionsContainer}>
                    <BillingPlanOptions
                        className="billingPlanOptionsSubmitOrCheckoutView"
                        billingCycle={billingCycle}
                        setBillingCycle={setBillingCycle}
                        partnerDiscount={partnerDiscount}
                        specificCurrencyCode={currencyCode}
                    />
                </div>

                {/* Simplified Price Display */}
                {!freeTrialOver && (<div className="pricingContainer" style={sty.pricingContainer}>
                    <>
                        <div className="pricingRow" style={sty.pricingRow}>
                            <span style={sty.freeTrialPrice}>
                                First 7 days free,
                            </span>
                            <span style={sty.regularPrice}>
                                then {formattedCurrency}{discountedFormattedAmount}/{formattedTimePeriod}
                            </span>
                        </div>
                        <div className="tagsRow" style={sty.tagsRow}>
                            <TagSymphony
                                className="emailReminderTag"
                                title="We'll email you 3 days before your trial ends"
                                color={TagColors.purple}
                                type="square"
                            />
                        </div>
                    </>

                </div>)}

                <StyledLabelP style={sty.disclaimer}>
                    {campaignDisclaimer} {trialText}
                </StyledLabelP>

                <ButtonSymphony
                    className="checkoutWithCardButton"
                    width="100%"
                    isLoading={isSubmittingCampaign}
                    disabled={isSubmittingCampaign || !isGeographyBucketsObjectValid || !canRunAds}
                    onClick={() => handleClickSubmitButton('upgradePlanCheckout')}
                >
                    {buttonText}
                </ButtonSymphony>

                {allowsDirectPayment && currencyCode === 'USD' &&
                    renderPaypalButton(false)
                }
            </div>
        )
    }

    /**
     * Renders the view for non-Pro users, showing upgrade options
     */
    const renderNonProView = () => {
        // Check if brand belongs to an organization and if organization is ready to use
        const showOrganizationUI = brandIdIsInOrganization(currentBrand?.id) &&
            primaryOrganization &&
            primaryOrganization.status === 'ready';

        const upgradeToProTabText = !freeTrialOver ? 'Start Free Trial' : 'Unlock Unlimited Campaigns';

        const renderView = () => {
            if (selectedTab === CheckoutType.ONE_OFF_CAMPAIGN) {
                return renderOneOffCampaignView()
            } else {
                return renderUpgradePlanView()
            }
        }

        return (
            <div>
                <div className="border-b">
                    <SymphonyTabs
                        data={[
                            {
                                value: CheckoutType.ONE_OFF_CAMPAIGN,
                                label: 'Pay for One-Off Campaign'
                            },
                            {
                                value: CheckoutType.UPGRADE_PLAN,
                                label: upgradeToProTabText
                            }
                        ]}
                        selectedValue={selectedTab}
                        onChange={(newValue) => setSelectedTab(newValue as CheckoutType)}
                    />
                </div>
                <div className="tabContentContainer" style={{
                    padding: 24
                }}>
                    {renderView()}
                </div>
            </div>
        )
    }

    let viewToRender;
    if (doesntRequireUpgrade) {
        viewToRender = renderProView()
    } else {
        viewToRender = isProBrand ? renderProView() : renderNonProView()
    }

    return (
        <div
            id="campaignSubmitButtonsContainer"
            className="campaignSubmitButtonsContainer submitOrCheckoutButtonsView"
            data-testid="campaign-submit-buttons"
        >
            {viewToRender}
        </div>
    );
}

export default SubmitOrCheckoutButtonsView